import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ContentCopy from '@mui/icons-material/ContentCopy';
import KeyOutlined from '@mui/icons-material/KeyOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { CompanyOnboardingStepKey } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { API_BASE_URL } from '~constants/consts';
import { FeatureFlags } from '~constants/featureFlags';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import CopyLlmPromptButton from '~pages/Settings/ApiClients/CopyLlmPromptButton';
import { ROUTES, settingsPagePath } from '~router/routePaths';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyName } from '~utils/user/user-utils';

import {
  Eyebrow,
  LabeledLine,
  OnboardingCard,
  OnboardingExplainer,
  OnboardingLink,
  SecondaryText,
  SettingsPageLink,
  TitleText,
} from '../CompanyOnboardingPrimitives';

const workAreaKey = (key: string) =>
  `company_onboarding.modules.agent_access.work_area.${key}`;

const SUGGESTED_PROMPT_KEYS = ['data_uploads', 'upload_order', 'field_usage'] as const;

const AI_FEATURE_KEYS = ['ai_reviews', 'ai_ticket_scanning'] as const;

// The agent ships a skill written for exactly this step. Naming it up front loads the
// playbook for the turn instead of leaving the model to route itself there from the prose.
const ONBOARDING_SKILL_COMMAND = '/onboarding-playbook';

const AssistantLauncherCard = () => {
  const { treadAgentStore } = useStores();
  const isTreadAgentEnabled = useFeatureFlag({ featureFlagKey: FeatureFlags.treadAgent });

  if (!isTreadAgentEnabled) {
    return null;
  }

  const startGuidedChat = (prompt: string, promptKind: 'launch' | 'suggestion') => {
    AnalyticsController.track('company_onboarding_guided_chat_started', {
      prompt_kind: promptKind,
      step_key: CompanyOnboardingStepKey.AGENT_ACCESS,
    });
    treadAgentStore.requestPrompt(`${ONBOARDING_SKILL_COMMAND} ${prompt}`);
  };

  return (
    <OnboardingCard data-test-id="company-onboarding-assistant" sx={{ mb: 3, p: 2 }}>
      <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1 }}>
        <AutoAwesomeOutlined
          sx={(theme) => ({ color: theme.semantic.linkText, fontSize: 18 })}
        />
        <Box>
          <TitleText>{t('company_onboarding.assistant_title')}</TitleText>
          <SecondaryText>{t('company_onboarding.assistant_subtitle')}</SecondaryText>
        </Box>
      </Box>
      <SmallButton
        color="brandV2Yellow"
        data-test-id="company-onboarding-assistant-start"
        onClick={() =>
          startGuidedChat(t('company_onboarding.assistant_launch_prompt'), 'launch')
        }
        startIcon={<ChatBubbleOutlineOutlined sx={{ fontSize: 14 }} />}
        variant="contained"
        sx={{ mt: 2 }}
      >
        {t('company_onboarding.actions.start_guided_chat')}
      </SmallButton>
      <Eyebrow sx={{ mt: 2 }}>
        {t('company_onboarding.assistant_suggestions_heading')}
      </Eyebrow>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {SUGGESTED_PROMPT_KEYS.map((promptKey) => {
          const prompt = t(
            `company_onboarding.modules.agent_access.prompts.${promptKey}`,
          );

          return (
            <ButtonBase
              data-test-id={`company-onboarding-assistant-prompt-${promptKey}`}
              key={promptKey}
              onClick={() => startGuidedChat(prompt, 'suggestion')}
              sx={(theme) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '999px',
                fontSize: 13,
                px: 1.5,
                py: 0.75,
                '&:hover': { backgroundColor: 'action.hover' },
              })}
            >
              {prompt}
            </ButtonBase>
          );
        })}
      </Box>
    </OnboardingCard>
  );
};

const AiFeaturesCard = () => (
  <OnboardingCard data-test-id="company-onboarding-ai-features" sx={{ mb: 3, p: 2 }}>
    <TitleText>{t(workAreaKey('ai_features.title'))}</TitleText>
    {AI_FEATURE_KEYS.map((featureKey) => (
      <Box
        data-test-id={`company-onboarding-ai-feature-${featureKey}`}
        key={featureKey}
        sx={{ borderTop: 1, borderColor: 'divider', mt: 1.5, pt: 1.5 }}
      >
        <OnboardingExplainer copyKey={workAreaKey(`ai_features.${featureKey}`)}>
          <LabeledLine label={t(workAreaKey('ai_features.setup'))} sx={{ mt: 0.5 }}>
            {t(workAreaKey(`ai_features.${featureKey}.setup`))}
          </LabeledLine>
          <SecondaryText sx={{ mt: 0.5 }}>
            {t(workAreaKey(`ai_features.${featureKey}.most`))}
          </SecondaryText>
        </OnboardingExplainer>
      </Box>
    ))}
    <SettingsPageLink
      data-test-id="company-onboarding-ai-features-open"
      settingsLink={ROUTES.settings.agents}
      sx={{ mt: 1.5 }}
    >
      {t(workAreaKey('ai_features.open'))}
    </SettingsPageLink>
  </OnboardingCard>
);

interface AgentAccessStepProps {
  companyId: string;
  settingsLink: string;
}

const AgentAccessStep = observer(({ companyId, settingsLink }: AgentAccessStepProps) => {
  const { userStore } = useStores();

  return (
    <Box data-test-id="company-onboarding-agent-access">
      <AssistantLauncherCard />
      <AiFeaturesCard />
      <OnboardingCard
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
        }}
      >
        <Box>
          <TitleText>{t(workAreaKey('advanced_heading'))}</TitleText>
          <SecondaryText sx={{ mt: 0.5 }}>
            {t(workAreaKey('advanced_body'))}
          </SecondaryText>
        </Box>
        <CopyLlmPromptButton
          apiBaseUrl={API_BASE_URL}
          companyId={companyId}
          companyName={getEffectiveUserCompanyName(userStore)}
          data-test-id="company-onboarding-agent-prompt-copy"
          onCopied={() =>
            AnalyticsController.track('company_onboarding_agent_prompt_copied', {
              step_key: CompanyOnboardingStepKey.AGENT_ACCESS,
            })
          }
          startIcon={<ContentCopy />}
        >
          {t(workAreaKey('copy_prompt'))}
        </CopyLlmPromptButton>
        <Typography>{t(workAreaKey('copy_prompt_hint'))}</Typography>
        <OnboardingLink
          to={settingsPagePath(settingsLink)}
          sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}
        >
          <KeyOutlined fontSize="small" />
          {t(workAreaKey('api_key_link'))}
        </OnboardingLink>
        <SecondaryText>{t(workAreaKey('key_warning'))}</SecondaryText>
        <SecondaryText>{t(workAreaKey('file_access'))}</SecondaryText>
      </OnboardingCard>
    </Box>
  );
});

export default AgentAccessStep;
